import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import TwellaLogoIcon from 'assets/icons/twellaLogo';
import backgroundSpreadImg from 'assets/img/backgroundSpreadImg.png';
import { useClerk, useSignIn } from '@clerk/clerk-react';

function SSOLogin() {
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();

  useEffect(() => {
    // Clear any existing session when entering SSO page
    signOut();
  }, [signOut]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(email);
    setIsEmailValid(isEmailValid);
  }, [email]);

  const handleSSOLogin = async () => {
    if (!isSignInLoaded || !email || !isEmailValid) return;

    setIsLoading(true);
    try {
      await signIn.authenticateWithRedirect({
        identifier: email,
        strategy: 'enterprise_sso',
        redirectUrl: '/auth/sso/callback',
        redirectUrlComplete: '/auth/sso/success',
      });
    } catch (error) {
      console.error('SSO authentication error:', error);
      let errorMessage =
        'Unable to authenticate with SSO. Please try again or contact support.';

      if (error.errors && error.errors.length > 0) {
        const errorCode = error.errors[0].code;

        if (errorCode === 'form_identifier_not_found') {
          errorMessage =
            "Your organization doesn't have SSO configured. Please contact your IT administrator or use regular login.";
        } else if (errorCode === 'form_conditional_param_value_disallowed') {
          errorMessage =
            "Your account requires SSO login. Please use 'Login with SSO' option instead.";
        }
      }

      toast({
        title: 'SSO Authentication Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      h="100vh"
      alignItems="center"
      px={{ base: '25px', md: '0px' }}
      flexDirection="column"
      bg={'#FFF8F3'}
      width={'100%'}
    >
      <Flex
        flexDirection={'column'}
        width={'80%'}
        alignItems={'center'}
        backgroundImage={backgroundSpreadImg}
        backgroundRepeat={'no-repeat'}
        backgroundSize={'cover'}
        backgroundPosition={'center'}
        height={'100vh'}
        pt={'68px'}
      >
        <Box mb={'58px'}>
          <TwellaLogoIcon width={'106px'} height={'38px'} />
        </Box>
        <Box>
          <Heading
            color={'#222222'}
            fontWeight={'700'}
            fontSize="40px"
            mb="24px"
            fontFamily={'DM Sans'}
          >
            Login with SSO
          </Heading>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          bg={'#FFFDFBCC'}
          p={'32px'}
          border={'1px solid #E7E7E7'}
          width={'564px'}
        >
          <FormControl as="form" onSubmit={(e) => e.preventDefault()}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="12px"
              fontWeight="700"
              color={'#222222'}
              mb="8px"
            >
              <Text>Email</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant="auth"
              fontSize="sm"
              ms={{ base: '0px', md: '0px' }}
              type="email"
              placeholder="name@company.com"
              mb="24px"
              fontWeight="500"
              height="44px"
              borderRadius={'8px'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              fontSize="14px"
              fontWeight="700"
              w="100%"
              h="40px"
              color={'white'}
              bg={isEmailValid ? '#EF6506' : '#EF650680'}
              _hover={isEmailValid ? { bg: '#FF7A1F' } : {}}
              borderRadius={'8px'}
              type="button"
              isDisabled={!isEmailValid || isLoading}
              onClick={handleSSOLogin}
              isLoading={isLoading}
            >
              Login with SSO
            </Button>
            <Flex
              justifyContent={'center'}
              alignItems={'center'}
              mt={'16px'}
              fontSize={'14px'}
              color={'#222222'}
            >
              <Text>Don't have SSO? &nbsp;</Text>
              <Text
                color={'#EF6506'}
                fontWeight={'600'}
                cursor={'pointer'}
                onClick={() => navigate('/auth/sign-in')}
                textDecoration={'underline'}
              >
                Use regular login
              </Text>
            </Flex>
          </FormControl>
        </Flex>

        <Flex
          flexDirection={'column'}
          pt={'15px'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={'8px'}
          fontSize={'14px'}
          lineHeight={'20px'}
          fontWeight={'500'}
          color={'#222222'}
        >
          <Flex>
            <Text>Need help with SSO login? </Text>
            <Text
              fontSize={'14px'}
              lineHeight={'20px'}
              fontWeight={'600'}
              color={'#EF6506'}
              letterSpacing={'0.1px'}
              ml={'4px'}
            >
              Contact support
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default SSOLogin;
